import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { useAuth } from '../contexts/AuthContext';
import { contactService } from '../services/contactService';
import { emailVerificationService } from '../services/emailVerificationService';
import { domainVerificationService } from '../services/domainVerificationService';
import { 
  UsersIcon, 
  EnvelopeIcon, 
  MapPinIcon, 
  XMarkIcon 
} from '@heroicons/react/24/outline';

type OnboardingStepId = 'contacts' | 'sender' | 'address';

interface OnboardingStep {
  id: OnboardingStepId;
  title: string;
  description: string;
  buttonText: string;
  path: string;
  icon: React.ComponentType<any>;
}

const OnboardingAlert: React.FC = () => {
  const navigate = useNavigate();
  const { currentWorkspace, activeUserId } = useWorkspace();
  const { currentUser } = useAuth();
  
  const [activeStep, setActiveStep] = useState<OnboardingStep | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isChecking, setIsChecking] = useState(false);

  const workspaceId = currentWorkspace?.id;
  const isCheckingRef = React.useRef(false);

  const checkOnboardingStatus = useCallback(async (isInitial = false) => {
    if (!workspaceId || (!activeUserId && !currentUser?.uid) || isCheckingRef.current) {
      return;
    }

    const userId = activeUserId || currentUser?.uid;
    if (!userId) return;

    // Small delay for initial check to let workspace data settle
    if (isInitial) {
      await new Promise(resolve => setTimeout(resolve, 1000));
    }

    // Double check we are still in the same workspace after the delay
    if (currentWorkspace?.id !== workspaceId) return;

    try {
      isCheckingRef.current = true;
      setIsChecking(true);
      
      let nextStep: OnboardingStep | null = null;

      // 1. Check Contacts
      const contacts = await contactService.getContacts(userId, workspaceId);
      const hasContacts = contacts.length > 0;
      const isContactsDismissed = localStorage.getItem(`onboarding_dismissed_${workspaceId}_contacts`) === 'true';
      
      if (!hasContacts && !isContactsDismissed) {
        nextStep = {
          id: 'contacts',
          title: 'Add your contacts',
          description: 'Bring in your audience by syncing contacts or importing them directly into Scubamail.',
          buttonText: 'Add contacts',
          path: `/workspace/${workspaceId}/contacts`,
          icon: UsersIcon
        };
      } else {
        // 2. Check Sender
        const [emails, domains] = await Promise.all([
          emailVerificationService.getUserVerifiedEmails(userId, workspaceId),
          domainVerificationService.getUserDomainVerifications(userId)
        ]);
        const hasVerifiedSender = emails.length > 0 || domains.some(d => d.status === 'verified');
        const isSenderDismissed = localStorage.getItem(`onboarding_dismissed_${workspaceId}_sender`) === 'true';
        
        if (!hasVerifiedSender && !isSenderDismissed) {
          nextStep = {
            id: 'sender',
            title: 'Verify a sender',
            description: 'Authenticate your domain to improve email deliverability and avoid spam filters.',
            buttonText: 'Verify sender',
            path: `/workspace/${workspaceId}/settings/senders/domains`,
            icon: EnvelopeIcon
          };
        } else {
          // 3. Check Address
          const address = currentWorkspace?.settings?.address;
          const hasAddress = !!(address?.addressLine1?.trim() && address?.city?.trim());
          const isAddressDismissed = localStorage.getItem(`onboarding_dismissed_${workspaceId}_address`) === 'true';
          
          if (!hasAddress && !isAddressDismissed) {
            nextStep = {
              id: 'address',
              title: 'Add your address',
              description: 'A physical mailing address is legally required for compliance with global email standards.',
              buttonText: 'Add address',
              path: `/workspace/${workspaceId}/settings/general`,
              icon: MapPinIcon
            };
          }
        }
      }

      // Final state update - only if we are still in the same workspace
      if (currentWorkspace?.id === workspaceId) {
        setActiveStep(nextStep);
        setIsVisible(!!nextStep);
      }
    } catch (err) {
      console.error('Error checking onboarding status:', err);
      setIsVisible(false);
    } finally {
      setIsChecking(false);
      isCheckingRef.current = false;
    }
  }, [workspaceId, activeUserId, currentUser?.uid, currentWorkspace]);

  useEffect(() => {
    // Reset visibility immediately on workspace switch to prevent flash of old alert
    setIsVisible(false);
    setActiveStep(null);
    
    if (workspaceId) {
      checkOnboardingStatus(true);
    }
  }, [workspaceId, checkOnboardingStatus]);

  const handleDismiss = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!activeStep || !workspaceId) return;
    
    localStorage.setItem(`onboarding_dismissed_${workspaceId}_${activeStep.id}`, 'true');
    setIsVisible(false);
    setActiveStep(null);
    
    // Check for the next step
    setTimeout(() => checkOnboardingStatus(false), 300);
  };

  const handleAction = () => {
    if (activeStep) {
      navigate(activeStep.path);
    }
  };

  if (!isVisible || !activeStep) return null;

  return (
    <div className="fixed bottom-3 left-3 right-3 sm:bottom-5 sm:left-5 sm:right-5 md:bottom-6 md:right-6 md:left-auto z-[100] w-auto md:w-full md:max-w-[500px] animate-slide-up outline-none">
      <div className="bg-white dark:bg-[#1E293B] border border-gray-100 dark:border-neutral-800 rounded-2xl sm:rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.12)] dark:shadow-[0_8px_30px_rgb(0,0,0,0.3)] overflow-hidden relative p-4 sm:p-5 md:p-6 group">
        {/* Close Button */}
        <button 
          onClick={handleDismiss}
          className="absolute top-3 right-3 sm:top-4 sm:right-4 md:top-5 md:right-5 text-gray-400 hover:text-gray-600 dark:text-neutral-500 dark:hover:text-neutral-300 transition-colors p-1"
        >
          <XMarkIcon className="w-5 h-5" />
        </button>

        <div className="flex items-start gap-3 sm:gap-4">
          {/* Icon Section */}
          <div className="flex-shrink-0 mt-0.5 sm:mt-1">
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl sm:rounded-2xl bg-gray-50 dark:bg-neutral-800 flex items-center justify-center border border-gray-100 dark:border-neutral-700">
                {activeStep.id === 'contacts' ? (
                   <span className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white">@</span>
                ) : (
                   <activeStep.icon className="w-5 h-5 sm:w-6 sm:h-6 text-gray-700 dark:text-gray-300" strokeWidth={2} />
                )}
            </div>
          </div>

          {/* Content Section */}
          <div className="flex-1 pr-6 sm:pr-8 md:pr-6 min-w-0">
            <h3 className="text-base sm:text-lg font-bold text-gray-900 dark:text-white leading-tight">
              {activeStep.title}
            </h3>
            <p className="mt-2 text-sm sm:text-[14px] leading-relaxed text-gray-600 dark:text-neutral-400">
              {activeStep.description}
            </p>
            
            <button
              onClick={handleAction}
              className="mt-4 w-full sm:w-auto bg-gray-900 hover:bg-black dark:bg-white dark:hover:bg-gray-100 text-white dark:text-gray-900 px-5 sm:px-6 py-2.5 rounded-xl text-sm font-bold transition-all duration-200 transform hover:scale-[1.02] active:scale-[0.98]"
            >
              {activeStep.buttonText}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingAlert;
